// src/pages/Enseignants/Events/EditEventModal.jsx
import React, { useEffect, useMemo, useState, useRef } from "react";
import { toast } from "react-toastify";
import {
  FaSave,
  FaTimes,
  FaPlus,
  FaTrash,
  FaCalendarAlt,
  FaMapMarkerAlt,
  FaLayerGroup,
  FaFlag,
  FaUsers,
  FaExternalLinkAlt,
  FaUpload,
  FaImage,
} from "react-icons/fa";
import Button from "../../../components/Button/Button";
import { updateEvent } from "../../../services/eventServices";
import { getSettings } from "../../../services/settingsServices";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

import {
  uploadEventPhoto,
  toAbsoluteEventPhotoUrl,
} from "../../../services/eventPhotoServices";

const quillModules = {
  toolbar: [
    [{ header: [false, 3, 4] }],
    ["bold", "italic", "underline"],
    [{ list: "ordered" }, { list: "bullet" }],
    ["link", "clean"],
  ],
};
const quillFormats = [
  "header",
  "bold",
  "italic",
  "underline",
  "list",
  "bullet",
  "link",
];

const EMPTY_FORM = {
  titre: "",
  description: "",
  start: "",
  end: "",
  type_event: "",
  level_event: "",
  cate_event: [],
  lieu_event: "",
  horaire_event: "",
  photo_url: "",
  agenda_url: "",
  is_active: true,
};

const FALLBACK = "/img/no-image.jpg";
const withBust = (url, v) =>
  url ? `${url}${url.includes("?") ? "&" : "?"}v=${v}` : url;

/** Convertit une date (ISO/SQL/etc.) en valeur input date locale YYYY-MM-DD */
function dateToInputValue(dt) {
  if (!dt) return "";
  const d = new Date(dt);
  if (Number.isNaN(d.getTime())) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/** Normalise un event venant du back pour le formulaire contrôlé */
function normalizeEventForForm(ev) {
  const cateArray = Array.isArray(ev?.cate_event)
    ? ev.cate_event
    : ev?.cate_event
      ? String(ev.cate_event)
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean)
      : [];

  return {
    titre: ev?.titre ?? "",
    description: ev?.description ?? "",
    start: dateToInputValue(ev?.start),
    end: dateToInputValue(ev?.end),
    type_event: ev?.type_event ?? "",
    level_event: ev?.level_event ?? "",
    cate_event: cateArray,
    lieu_event: ev?.lieu_event ?? "",
    horaire_event: ev?.horaire_event ?? "",
    photo_url: ev?.photo_url ?? "",
    agenda_url: ev?.agenda_url ?? "",
    is_active: Boolean(ev?.is_active ?? true),
  };
}

const EditEventModal = ({ isOpen, onClose, refreshList, event }) => {
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [selectedCate, setSelectedCate] = useState("");
  const [shortnameClub, setShortnameClub] = useState("");
  const [imgOk, setImgOk] = useState(true);

  // Upload sous-modale (comme EditNewsModal)
  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [outputFormat, setOutputFormat] = useState("webp"); // 'webp' | 'jpg' | 'png'
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  // Cache-busting sur l'aperçu
  const [imgVersion, setImgVersion] = useState(0);

  const modalRef = useRef(null);

  // Récup paramètres (shortname du club)
  useEffect(() => {
    if (!isOpen) return;
    (async () => {
      try {
        const data = await getSettings();
        if (data?.length > 0) setShortnameClub(data[0].shortname_club || "");
      } catch (err) {
        console.error("Erreur lors de la récupération des paramètres", err);
        toast.error("Erreur lors de la récupération des paramètres.");
      }
    })();
  }, [isOpen]);

  // Ré-initialise à l’ouverture/changement d’event
  useEffect(() => {
    if (!isOpen) return;
    setFormData(event ? normalizeEventForForm(event) : EMPTY_FORM);
    setSelectedCate("");
    setImgOk(true);
    setImgVersion((v) => v + 1);
    setIsUploadOpen(false);
    setSelectedFile(null);
    setUploading(false);
    setUploadProgress(0);
  }, [isOpen, event?.id]);

  // ESC pour fermer (bloque si sous-modale ouverte)
  useEffect(() => {
    if (!isOpen) return;
    const onEsc = (e) => {
      if (e.key !== "Escape") return;
      if (isUploadOpen) {
        setIsUploadOpen(false);
        return;
      }
      handleClose();
    };
    window.addEventListener("keydown", onEsc);
    return () => window.removeEventListener("keydown", onEsc);
  }, [isOpen, isUploadOpen]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? !!checked : (value ?? ""),
    }));
  };

  const handleAddCategory = () => {
    if (selectedCate && !formData.cate_event.includes(selectedCate)) {
      setFormData((prev) => ({
        ...prev,
        cate_event: [...prev.cate_event, selectedCate],
      }));
      setSelectedCate("");
    }
  };
  const handleRemoveCategory = (category) => {
    setFormData((prev) => ({
      ...prev,
      cate_event: prev.cate_event.filter((c) => c !== category),
    }));
  };
  const handleClearCategories = () => {
    setFormData((prev) => ({ ...prev, cate_event: [] }));
  };

  const validateBeforeSubmit = () => {
    if (!formData.titre?.trim()) {
      toast.error("Le titre est requis.");
      return false;
    }
    if (!formData.start) {
      toast.error("La date de début est requise.");
      return false;
    }
    if (formData.end && new Date(formData.end) < new Date(formData.start)) {
      toast.error(
        "La date de fin ne peut pas être antérieure à la date de début.",
      );
      return false;
    }
    if (!formData.type_event) {
      toast.error("Le type d’événement est requis.");
      return false;
    }
    if (!formData.level_event) {
      toast.error("Le niveau de l’événement est requis.");
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateBeforeSubmit()) return;

    try {
      const payload = {
        ...formData,
        cate_event: (formData.cate_event || []).join(","),
      };
      await updateEvent(event.id, payload);
      toast.success("Événement mis à jour avec succès.");
      await refreshList?.();
      handleClose();
    } catch (error) {
      console.error("Erreur lors de la mise à jour de l'événement", error);
      toast.error("Erreur lors de la mise à jour de l'événement.");
    }
  };

  const handleClose = () => {
    setFormData(EMPTY_FORM);
    setSelectedCate("");
    setImgOk(true);
    setIsUploadOpen(false);
    setSelectedFile(null);
    setUploading(false);
    setUploadProgress(0);
    onClose?.();
  };

  // Listes avec injection du shortname club
  const typeEvents = useMemo(
    () => [
      "Compétition",
      "Animation",
      "Stage & Regroupement Technique",
      "Passage de Grade",
      "Entraînement Départemental",
      "Stage Sportif Départemental",
      "Tournoi Interclubs",
      "Cours commun Club",
      "Stage Club",
      shortnameClub || "Votre Club",
      "Autre",
    ],
    [shortnameClub],
  );
  const levelEvents = useMemo(
    () => ["Club", "Départemental", "Régional", "National", "International"],
    [],
  );
  const cateEvents = useMemo(
    () => [
      "Eveil",
      "Mini-Poussin(e)",
      "Poussin(e)",
      "Benjamin(e)",
      "Minime",
      "Cadet(te)",
      "Junior",
      "Sénior",
      "Vétéran",
    ],
    [],
  );

  if (!isOpen) return null;

  const formatFR = (d) =>
    d
      ? new Date(d).toLocaleDateString("fr-FR", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
        })
      : "—";

  // Image preview (absolutisation + cache-bust)
  const coverAbs = toAbsoluteEventPhotoUrl(
    formData.photo_url?.trim() ? formData.photo_url : FALLBACK,
  );
  const cover = withBust(coverAbs, imgVersion);

  // Upload handlers (sous-modale)
  const onFileChange = (e) => setSelectedFile(e.target.files?.[0] || null);

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!event?.id) {
      toast.error(
        "Impossible d’associer l’image : id de l’événement manquant.",
      );
      return;
    }
    if (!selectedFile) {
      toast.warn("Sélectionnez un fichier image.");
      return;
    }
    // Validation front (comme news)
    const okType = /^image\/(png|jpe?g|webp)$/i.test(selectedFile.type);
    if (!okType) {
      toast.error("Format d'image non supporté. Utilisez PNG / JPG / WEBP.");
      return;
    }
    if (selectedFile.size > 10 * 1024 * 1024) {
      toast.error("Fichier trop volumineux (max 10 Mo).");
      return;
    }

    try {
      setUploading(true);
      setUploadProgress(0);

      const updated = await uploadEventPhoto(event.id, selectedFile, {
        format: outputFormat,
        onUploadProgress: (evt) => {
          if (!evt.total) return;
          const p = Math.round((evt.loaded / evt.total) * 100);
          setUploadProgress(p);
        },
      });

      // back renvoie { id, photo_url } (chemin /uploads/...)
      setFormData((p) => ({
        ...p,
        photo_url: updated?.photo_url || p.photo_url,
      }));
      setImgVersion((v) => v + 1);
      toast.success("Image téléversée et optimisée.");

      // reset sous-modale
      setIsUploadOpen(false);
      setSelectedFile(null);
    } catch (err) {
      console.error("Upload image event:", err);
      toast.error("Erreur lors du téléversement de l’image.");
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const onBackdrop = (e) => {
    if (e.target !== e.currentTarget) return;
    if (isUploadOpen) return; // ne pas fermer le parent si sous-modale ouverte
    handleClose();
  };

  return (
    <div
      className="fixed inset-0 z-[70] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onBackdrop}
      role="dialog"
      aria-modal="true"
      aria-label="Modifier un événement"
    >
      {/* Boîte modale 85vh, header sticky, scroll vertical */}
      <div
        ref={modalRef}
        className="relative w-full max-w-5xl h-[85vh] max-h-[85vh] overflow-y-auto rounded-2xl shadow-xl ring-1 ring-slate-200/70 dark:ring-slate-800/70 bg-white/90 dark:bg-slate-900/80"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 z-10 flex items-center justify-between px-5 sm:px-7 py-4 bg-gradient-to-r from-blue-500 via-violet-500 to-pink-500 text-white">
          <h3 className="text-base sm:text-lg font-semibold">
            Modifier un événement
          </h3>
          <button
            type="button"
            onClick={handleClose}
            className="inline-flex items-center justify-center h-10 w-10 rounded-full bg-white/20 hover:bg-white/30 transition"
            aria-label="Fermer"
            title="Fermer"
          >
            <FaTimes />
          </button>
        </div>

        {/* Corps */}
        <div className="grid md:grid-cols-3 gap-0">
          {/* Colonne Form (2/3) */}
          <div className="md:col-span-2 p-5 sm:p-7">
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Titre */}
              <div>
                <label className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
                  Titre de l’événement *
                </label>
                <input
                  type="text"
                  name="titre"
                  value={formData.titre}
                  onChange={handleChange}
                  className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
                  placeholder="Ex : Compétition départementale"
                  required
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
                  Description
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  rows={4}
                  className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
                  placeholder="Informations utiles, consignes, documents..."
                />
              </div>

              {/* Dates */}
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
                    Date de début *
                  </label>
                  <input
                    type="date"
                    name="start"
                    value={formData.start || ""}
                    onChange={handleChange}
                    className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
                    Date de fin
                  </label>
                  <input
                    type="date"
                    name="end"
                    value={formData.end || ""}
                    onChange={handleChange}
                    className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
                  />
                </div>
              </div>

              {/* Type & Niveau */}
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
                    Type *
                  </label>
                  <select
                    name="type_event"
                    value={formData.type_event}
                    onChange={handleChange}
                    className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
                    required
                  >
                    <option value="">Sélectionner</option>
                    {typeEvents.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
                    Niveau *
                  </label>
                  <select
                    name="level_event"
                    value={formData.level_event}
                    onChange={handleChange}
                    className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
                    required
                  >
                    <option value="">Sélectionner</option>
                    {levelEvents.map((l) => (
                      <option key={l} value={l}>
                        {l}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Catégories */}
              <div>
                <label className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
                  Catégories
                </label>
                <div className="flex items-center gap-2">
                  <select
                    value={selectedCate}
                    onChange={(e) => setSelectedCate(e.target.value)}
                    className="flex-1 rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
                  >
                    <option value="">Sélectionner une catégorie</option>
                    {cateEvents.map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={handleAddCategory}
                    className="btn btn-primary rounded-xl"
                  >
                    <FaPlus />
                  </button>
                  {formData.cate_event.length > 0 && (
                    <button
                      type="button"
                      onClick={handleClearCategories}
                      className="text-xs underline opacity-80 hover:opacity-100"
                    >
                      Vider
                    </button>
                  )}
                </div>

                {/* Chips */}
                {formData.cate_event.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {formData.cate_event.map((cate) => (
                      <span
                        key={cate}
                        className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full text-xs bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-200 ring-1 ring-sky-200/70 dark:ring-sky-800/50"
                      >
                        {cate}
                        <button
                          type="button"
                          onClick={() => handleRemoveCategory(cate)}
                          className="opacity-80 hover:opacity-100"
                          aria-label={`Retirer ${cate}`}
                          title={`Retirer ${cate}`}
                        >
                          <FaTrash />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Lieu */}
              <div>
                <label className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
                  Lieu
                </label>
                <input
                  type="text"
                  name="lieu_event"
                  value={formData.lieu_event}
                  onChange={handleChange}
                  className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
                  placeholder="Salle / adresse"
                />
              </div>

              {/* Horaires (Quill) */}
              <div>
                <label className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
                  Horaires (formaté)
                </label>
                <div className="rounded-xl overflow-hidden ring-1 ring-slate-300 dark:ring-slate-700">
                  <ReactQuill
                    key={`rq-${isOpen}-${event?.id || "edit"}`}
                    value={formData.horaire_event}
                    onChange={(value) =>
                      setFormData((prev) => ({ ...prev, horaire_event: value }))
                    }
                    modules={quillModules}
                    formats={quillFormats}
                    placeholder="Ex : Pesée 8h30, Début 9h30..."
                    theme="snow"
                  />
                </div>
              </div>

              {/* URL image & agenda */}
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
                    URL de la photo
                  </label>
                  <input
                    type="text"
                    name="photo_url"
                    value={formData.photo_url}
                    onChange={(e) => {
                      setImgOk(true);
                      handleChange(e);
                      setImgVersion((v) => v + 1); // MAJ aperçu si on colle une URL manuelle
                    }}
                    className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
                    placeholder="Rempli automatiquement après upload (ou collez une URL)"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
                    URL agenda (Google/ICS)
                  </label>
                  <input
                    type="url"
                    name="agenda_url"
                    value={formData.agenda_url}
                    onChange={handleChange}
                    className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
                    placeholder="https://…"
                  />
                </div>
              </div>

              {/* Actif */}
              <div className="flex items-center gap-2">
                <input
                  id="is_active_edit"
                  type="checkbox"
                  name="is_active"
                  checked={!!formData.is_active}
                  onChange={handleChange}
                  className="toggle toggle-success"
                />
                <label htmlFor="is_active_edit" className="text-sm">
                  Afficher l’événement (actif)
                </label>
              </div>

              {/* Actions */}
              <div className="pt-2 flex justify-center gap-2">
                <Button
                  text="Enregistrer"
                  icon={FaSave}
                  type="submit"
                  className="!rounded-xl gap-2"
                />
                <Button
                  text="Annuler"
                  icon={FaTimes}
                  onClick={handleClose}
                  type="button"
                  className="btn-ghost !rounded-xl gap-2"
                />
              </div>
            </form>
          </div>

          {/* Colonne Aperçu (1/3) */}
          <aside className="md:col-span-1 p-5 sm:p-7 bg-slate-50/60 dark:bg-slate-900/40 border-l border-slate-200 dark:border-slate-800">
            <div className="rounded-xl overflow-hidden ring-1 ring-slate-200 dark:ring-slate-800 bg-white/80 dark:bg-slate-900/60">
              <div className="relative p-3">
                <img
                  src={cover}
                  alt={formData.titre || "Aperçu"}
                  className="block w-full max-h-[38vh] object-contain rounded-md"
                  onError={(e) => {
                    setImgOk(false);
                    e.currentTarget.src = FALLBACK;
                  }}
                />
                <span className="absolute -bottom-2 -right-2 h-8 w-8 rounded-full grid place-items-center bg-slate-900 text-white dark:bg-slate-200 dark:text-slate-900 shadow ring-2 ring-white dark:ring-slate-900">
                  <FaImage className="text-xs" />
                </span>
              </div>

              <div className="px-4 pb-3">
                <Button
                  text="Téléverser une image"
                  icon={FaUpload}
                  className="!rounded-xl gap-2 w-full"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setIsUploadOpen(true);
                  }}
                />
              </div>

              <div className="px-4 pb-4 text-sm text-slate-700 dark:text-slate-200 space-y-2">
                {formData.type_event && (
                  <div className="flex items-center gap-2">
                    <FaLayerGroup className="opacity-70" />
                    <span className="truncate">{formData.type_event}</span>
                  </div>
                )}
                {formData.level_event && (
                  <div className="flex items-center gap-2">
                    <FaFlag className="opacity-70" />
                    <span className="truncate">{formData.level_event}</span>
                  </div>
                )}
                {formData.lieu_event && (
                  <div className="flex items-center gap-2">
                    <FaMapMarkerAlt className="opacity-70" />
                    <span className="truncate">{formData.lieu_event}</span>
                  </div>
                )}
                {formData.cate_event?.length > 0 && (
                  <div className="flex items-center gap-2">
                    <FaUsers className="opacity-70" />
                    <span className="truncate">
                      {formData.cate_event.join(", ")}
                    </span>
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <FaCalendarAlt className="opacity-70" />
                  <span className="truncate">
                    {formatFR(formData.start)}
                    {formData.end ? ` → ${formatFR(formData.end)}` : ""}
                  </span>
                </div>
                {formData.agenda_url && (
                  <a
                    href={formData.agenda_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-xs underline decoration-dotted mt-1"
                    title="Ouvrir l’agenda"
                  >
                    Ouvrir l’agenda <FaExternalLinkAlt className="opacity-70" />
                  </a>
                )}
              </div>
            </div>
          </aside>
        </div>
      </div>

      {/* Sous-modale upload image (local sharp ≤1600) */}
      {isUploadOpen && (
        <div
          className="fixed inset-0 z-[80] flex items-center justify-center bg-black/40 p-4"
          onClick={(e) =>
            e.currentTarget === e.target && setIsUploadOpen(false)
          }
          role="dialog"
          aria-modal="true"
          aria-label="Téléverser une image d'événement"
        >
          <div
            className="w-full max-w-md rounded-2xl overflow-hidden shadow-2xl bg-white dark:bg-slate-900 ring-1 ring-slate-200 dark:ring-slate-800 p-5"
            onClick={(e) => e.stopPropagation()}
          >
            <h4 className="text-slate-900 dark:text-white font-semibold mb-3">
              Téléverser une image (optimisée ≤1600px)
            </h4>

            <form onSubmit={handleUpload} className="space-y-4">
              <input
                type="file"
                accept="image/*"
                onChange={onFileChange}
                className="block w-full text-sm text-slate-700 dark:text-slate-200"
              />

              <div className="flex items-center gap-2">
                <span className="text-sm">Format</span>
                <select
                  className="rounded-lg border border-slate-300 dark:border-slate-700 bg-white/80 dark:bg-slate-900/70 px-2 py-1 text-sm"
                  value={outputFormat}
                  onChange={(e) => setOutputFormat(e.target.value)}
                  disabled={uploading}
                >
                  <option value="webp">WEBP (recommandé)</option>
                  <option value="jpg">JPG</option>
                  <option value="png">PNG</option>
                </select>
              </div>

              {uploading && (
                <div>
                  <progress
                    className="progress w-full"
                    value={uploadProgress}
                    max="100"
                  />
                  <div className="text-xs mt-1">{uploadProgress}%</div>
                </div>
              )}

              <div className="flex items-center justify-end gap-2">
                <Button
                  text="Fermer"
                  icon={FaTimes}
                  type="button" // ✅ important : ne doit pas soumettre le form
                  className="!rounded-xl"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setIsUploadOpen(false);
                  }}
                  disabled={uploading}
                />

                <Button
                  text={uploading ? "Transfert…" : "Sauvegarder"}
                  icon={FaSave}
                  type="submit" // ✅ important : déclenche onSubmit={handleUpload}
                  className="!rounded-xl"
                  disabled={!selectedFile || uploading} // ✅ évite “rien ne se passe”
                />
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default EditEventModal;
